import { Option, OptionList, OptionListProps } from "@introist/react-foundation/v2";
import { useCallback, useEffect, useMemo, useState } from "react";
import { compact } from "lodash";

export type TraversableOption = Option & {
  path?: boolean;
};

type TraversableOptionListProps = Omit<OptionListProps, "options"> & {
  options: (path: string) => Promise<TraversableOption[]>;
};

export const TraversableOptionList = (props: TraversableOptionListProps) => {
  const { options, ...rest } = props;

  const [cache, setCache] = useState<{ [path: string]: TraversableOption[] }>({});

  const [path, setPath] = useState<string>("root");
  const [currentOptions, setCurrentOptions] = useState<TraversableOption[] | null>(null);

  const loadPath = useCallback(async () => {
    if (cache[path]) {
      setCurrentOptions(cache[path]);
    } else {
      setCurrentOptions(null);
      const opts = await options(path);
      cache[path] = opts;
      setCache({ ...cache });
      setCurrentOptions(opts);
    }
  }, [path, cache, options]);

  useEffect(() => {
    loadPath();
  }, [path, loadPath]);

  const onItemClickWithTraverse = (option: TraversableOption) => {
    if (option.path) setPath(`${path}.${option.key}`);
    else props.onItemClick?.(option);
  };

  const back = useMemo(() => {
    if (path === "root") return null;
    return {
      key: "back",
      title: "Back",
      startAdornmentIcon: "arrowLeft",
      onClick: () => {
        const newPath = path.split(".").slice(0, -1).join(".");
        setPath(newPath);
      }
    } as Option;
  }, [path]);

  return (
    <OptionList
      searchable
      searchPlaceholder="Search"
      options={currentOptions ?? []}
      loading={currentOptions === null}
      {...rest}
      actions={compact([back, ...(props.actions ?? [])])}
      onItemClick={onItemClickWithTraverse}
    />
  );
};
